
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';
import { ExamQuestion } from '@/hooks/useExamQuestions';

interface QuestionPreviewDialogProps {
  question: ExamQuestion;
  isOpen: boolean;
  onClose: () => void;
}

export function QuestionPreviewDialog({ question, isOpen, onClose }: QuestionPreviewDialogProps) {
  const isCorrectOption = (option: string) => {
    return question.correct_answer === option;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Question Preview
            <Badge variant={question.is_active ? "default" : "secondary"}>
              {question.is_active ? "Active" : "Inactive"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Module {question.module_number}, Part {question.part_number} • {question.language}
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">Question:</h3>
            <p className="text-sm leading-relaxed">{question.question_text}</p>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">Answer Options:</h4>
            
            <div className={`flex items-center gap-2 p-3 rounded border ${isCorrectOption('A') ? 'bg-green-50 border-green-200' : 'bg-background'}`}>
              <span className="font-medium">A.</span>
              <span className="flex-1">{question.option_a}</span>
              {isCorrectOption('A') && <CheckCircle className="h-4 w-4 text-green-600" />}
            </div>

            <div className={`flex items-center gap-2 p-3 rounded border ${isCorrectOption('B') ? 'bg-green-50 border-green-200' : 'bg-background'}`}>
              <span className="font-medium">B.</span>
              <span className="flex-1">{question.option_b}</span>
              {isCorrectOption('B') && <CheckCircle className="h-4 w-4 text-green-600" />}
            </div>

            {question.option_c && (
              <div className={`flex items-center gap-2 p-3 rounded border ${isCorrectOption('C') ? 'bg-green-50 border-green-200' : 'bg-background'}`}>
                <span className="font-medium">C.</span>
                <span className="flex-1">{question.option_c}</span>
                {isCorrectOption('C') && <CheckCircle className="h-4 w-4 text-green-600" />}
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground border-t pt-4">
            <strong>Correct Answer:</strong> {question.correct_answer}
          </div>

          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
